<script setup lang="ts">
import { ref, watch } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import TimesIcon from '@primeicons/vue/times';
import { AccountSearchFields, useAccounts, useToastNotifications } from '@apparatix/composables';
import { AccountTypes, AssociationAccount } from '@apparatix/types/Leads';
import CreateAccount from '@apparatix/library/CreateAccount.vue';
import { useOpportunityCreationStore } from '@apparatix/stores/useOpportunityCreationStore';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { Account, AccountOverview } from '@apparatix/types/Account';
import AssociationListItem from '../AssociationListItem.vue';
import ContactTypeChip from '../ContactTypeChip.vue';
import AccountConflicts from '../AccountConflicts.vue';
import { newAccountPayloadTransformer, PendingAccount, transformToAssociation } from '@apparatix/schemas/Account';

const { createAccount, searchAccounts } = useAccounts();
const store = useOpportunityCreationStore();
const opportunityStore = useOpportunityStore();
const { showError } = useToastNotifications();

interface AccountsProps {
  industryCodeRequired?: boolean;
}

const props = defineProps<AccountsProps>();

const createComponentInstance = ref();
const potentialAccountConflicts = ref<AssociationAccount[]>([]);

const hasPotentialConflicts = async (account: PendingAccount) => new Promise<boolean>(resolve => {
  const searchParams: AccountSearchFields = {
    name: account.name,
    mode: 'startswith',
  };
  searchAccounts(searchParams, results => {
    const filteredResults = (results || []).filter(item => item.c2Id !== account.id);

    if (!filteredResults?.length) {
      potentialAccountConflicts.value = [];
      resolve(false);
    } else {
      potentialAccountConflicts.value = filteredResults;
      resolve(true);
    }
  });
});

const onCreateAccount = async (account: PendingAccount) => {
  accountConflictContinueText.value = 'Dismiss Suggestions & Create New Account';
  account.type = getAccountType(account.type as AccountTypes);
  store.isLoading = true;

  const hasConflicts = await hasPotentialConflicts(account);
  if (hasConflicts) {
    return;
  }

  await saveNewAccount(account);
};

const onAfterAccountCreated = (account: AssociationAccount) => {
  store.addAccount(account);
  createComponentInstance.value.reset();
  store.isLoading = false;
};

const saveNewAccount = async (account: PendingAccount) => {
  try {
    const accountPayload = newAccountPayloadTransformer.parse(account);
    const result = await createAccount(accountPayload);
    if (result?.id) {
      const newAccount = newAccountToAssociation({
        // eslint-disable-next-line camelcase
        company_name: result.name,
        type: result.type,
        addresses: account.addresses?.map(address => ({
          // eslint-disable-next-line camelcase
          address_line1: address.addressLine1 ?? '',
          // eslint-disable-next-line camelcase
          address_line2: address.addressLine2 ?? '',
          // eslint-disable-next-line camelcase
          address_line3: address.addressLine3 ?? '',
          city: address.city ?? '',
          region: address.region ?? '',
          // eslint-disable-next-line camelcase
          postal_code: address.postalCode ?? '',
        })) ?? [],
      }, { c2Id: result.id, ownerId: opportunityStore.currentUser.id });
      onAfterAccountCreated(newAccount);
    }
  } catch {
    showError(`Unable to save ${account?.name ?? ''} as new Account`);
  }
};

const newAccountToAssociation = (account: Account, response: Partial<AccountOverview>): AssociationAccount => {
  const newAccount: Partial<AssociationAccount> = {
    c2Id: response.c2Id,
    c2Name: account.company_name,
    c2RawType: getAccountType(account.type as AccountTypes),
    c2Type: getDisplayName(account.type as string),
  };
  if (account.addresses?.length) {
    newAccount.addresses = account.addresses;
  }
  if (account.address_emails?.length) {
    newAccount.addressEmails = account.address_emails;
  }
  if (account.address_phone_numbers?.length) {
    newAccount.addressPhoneNumbers = account.address_phone_numbers;
  }

  return newAccount as AssociationAccount;
};

const getAccountType = (originalType: AccountTypes): AccountTypes => {
  if (originalType !== 'Advertiser') {
    return originalType;
  }
  if (opportunityStore.createOpportunityContext === 'create-lead' || opportunityStore.createOpportunityContext === 'convert-lead') {
    return 'Lead';
  }
  if (
    opportunityStore.createOpportunityContext === 'create-opportunity' ||
    opportunityStore.createOpportunityContext === 'editing'
  ) {
    return 'Advertiser';
  }
  return undefined;
};

const getDisplayName = (originalType: string): string => {
  if (originalType === 'Advertiser') {
    return 'Prospective Advertiser';
  }

  if (originalType === 'Lead') {
    return 'Unqualified Advertiser';
  }
  return originalType;
};

const onSelectAccount = (account: PendingAccount) => {
  try {
    const association = transformToAssociation.parse(account) as AssociationAccount;
    store.addAccount(association);
    createComponentInstance.value.reset();
  } catch (e) {
    console.log(e);
    showError(`Unable to add ${account?.name ?? ''} to this Opportunity`);
  }
};

const onReplaceAccount = (account) => {
  try {
    const replaceAccount = store.accounts.find(act => act.c2RawType === 'Lead');
    store.removeAccount(replaceAccount);
    store.addAccount(account);
  } catch (e) {
    console.log(e);
    showError(`Unable to replace ${account?.name ?? ''} on this Opportunity`);
  }
};

const acceptConflictSuggestion = (account: AssociationAccount) => {
  store.isLoading = false;
  isConversionConflictCheck.value = false;
  potentialAccountConflicts.value = [];
  onReplaceAccount(account);
};

const dismissConflictSuggestions = async () => {
  store.isLoading = false;
  potentialAccountConflicts.value = [];

  if (isConversionConflictCheck.value) {
    isConversionConflictCheck.value = false;
    return;
  }
  const account = await createComponentInstance.value.getFormValues();
  await saveNewAccount(account);
};

const accountConflictContinueText = ref('Proceed with Current Account');
const isConversionConflictCheck = ref(false);

watch(
  () => store.accounts,
  async (accounts) => {
    for (const account of accounts) {
      if (account.c2RawType === 'Lead') {
        isConversionConflictCheck.value = true;
        const hasConflicts = await hasPotentialConflicts({ name: account.c2Name, id: account.c2Id });
        if (hasConflicts) {
          // stop processing further accounts
          break;
        }
      }
    }
  },
);

</script>
<template>
  <div class="flex flex-col lg:flex-row-reverse gap-4 lg:gap-x-6 w-full">
    <div class="flex-initial flex flex-col gap-3 w-full lg:w-1/2">
      <h2 class="font-medium text-sm text-[var(--swimlane-secondary-text)]">
        Accounts
      </h2>
      <template v-if="!potentialAccountConflicts.length">
        <div v-if="store.accounts.length" class="flex flex-col gap-4">
          <AssociationListItem
            v-for="account in store.accounts"
            :key="account.c2Id"
            association-type="account"
            :title="account.c2Name"
          >
            <div class="flex items-center gap-2 justify-end">
              <Button
                icon-only
                severity="secondary"
                text
                rounded
                aria-label="Remove"
                @click="store.removeAccount(account)"
              >
                <TimesIcon />
              </Button>
            </div>
            <template #footer>
              <div class="w-auto">
                <ContactTypeChip size="small" :label="account.c2Type" />
              </div>
            </template>
          </AssociationListItem>
        </div>
        <Message v-for="(error, idx) in store.errorBag.accounts" :key="idx" severity="error">
          {{ error }}
        </Message>
      </template>
      <template v-if="potentialAccountConflicts.length">
        <AccountConflicts
          select-button-label="Select"
          :dismiss-button-label="accountConflictContinueText"
          :conflicts="potentialAccountConflicts"
          :allow-dismiss="true"
          @account-selected="acceptConflictSuggestion"
          @cancel="dismissConflictSuggestions"
        />
      </template>
    </div>
    <div class="flex-initial w-full lg:w-1/2">
      <CreateAccount
        ref="createComponentInstance"
        :default-type="'Advertiser'"
        :allowed-selection-types="store.accountTypesValidForSelection"
        :allowed-creation-types="store.accountTypesValidForCreation"
        :is-loading="store.isLoading"
        :disabled="Boolean(store.accounts.find(account => account.c2Type === 'Advertiser'))"
        :use-advertiser-search="Boolean(store.agencyAccountId)"
        :agency-id="store.agencyAccountId"
        :industry-code-required="props.industryCodeRequired"
        :allow-agency-in-wizard="true"
        @on:submit="onCreateAccount"
        @on:account-selected="onSelectAccount"
      />
    </div>
  </div>
</template>
